// frontend/src/components/Clinics/Clinics.js

import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import './Clinics.css';

function Clinics() {
  const [clinics, setClinics] = useState([]);
  const [form, setForm] = useState({
    clinic_name: '',
    journal_system: '', // <-- default (ingen överföring)
    use_internal_journal: false, // Aktiverar inbyggt journalsystem
    use_sports_module: false, // Aktiverar Aion Sports
    sms_sender: '', // SMS-avsändarnamn (max 11 tecken)
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editClinicId, setEditClinicId] = useState(null);

  // Globala systeminställningar
  const [whisperBackend, setWhisperBackend] = useState('azure');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchClinics();
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const resp = await apiClient.get('/api/system-settings');
      if (resp.data.whisper_backend_default) {
        setWhisperBackend(resp.data.whisper_backend_default);
      }
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const handleWhisperBackendChange = async (value) => {
    setWhisperBackend(value);
    setSavingSettings(true);
    try {
      await apiClient.put('/api/system-settings', {
        whisper_backend_default: value
      });
    } catch (error) {
      console.error('Error saving system settings:', error);
      alert('Kunde inte spara inställningen.');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get('/api/clinics');
      setClinics(response.data);
    } catch (error) {
      console.error('Error fetching clinics:', error);
      alert('Ett fel uppstod vid hämtning av kliniker.');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name.trim()) {
      alert('Klinikens namn får inte vara tomt.');
      return;
    }

    try {
      if (isEditing) {
        // Vid uppdatering
        await apiClient.put(`/api/clinics/${editClinicId}`, form);
        alert('Klinik uppdaterad.');
      } else {
        // Skapa ny klinik
        await apiClient.post('/api/clinics', form);
        alert('Klinik skapad.');
      }
      // Rensa formuläret
      setForm({ clinic_name: '', journal_system: '', use_internal_journal: false, use_sports_module: false, sms_sender: '' });
      setIsEditing(false);
      setEditClinicId(null);
      // Hämta uppdaterad lista
      fetchClinics();
    } catch (error) {
      console.error('Error saving clinic:', error);
      alert('Ett fel uppstod vid sparande av klinik.');
    }
  };

  const handleEdit = (clinic) => {
    // När vi klickar "Redigera" på en rad, sätt formet
    setForm({
      clinic_name: clinic.clinic_name,
      journal_system: clinic.journal_system || '',
      use_internal_journal: clinic.use_internal_journal || false,
      use_sports_module: clinic.use_sports_module || false,
      sms_sender: clinic.sms_sender || '',
    });
    setIsEditing(true);
    setEditClinicId(clinic.clinic_id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill ta bort denna klinik?')) {
      try {
        await apiClient.delete(`/api/clinics/${id}`);
        alert('Klinik borttagen.');
        fetchClinics();
      } catch (error) {
        console.error('Error deleting clinic:', error);
        alert('Ett fel uppstod vid borttagning av klinik.');
      }
    }
  };

  const handleCancelEdit = () => {
    setForm({ clinic_name: '', journal_system: '', use_internal_journal: false, use_sports_module: false, sms_sender: '' });
    setIsEditing(false);
    setEditClinicId(null);
  };

  return (
    <div className="clinics-container">
      <h2>Klinikhantering</h2>

      {/* Globala systeminställningar */}
      <div className="system-settings-section">
        <h3>Systeminställningar</h3>
        <div className="system-setting-row">
          <label htmlFor="whisper-backend-select">Transkribering (Whisper-backend):</label>
          <select
            id="whisper-backend-select"
            value={whisperBackend}
            onChange={(e) => handleWhisperBackendChange(e.target.value)}
            disabled={savingSettings}
            className="clinic-select"
          >
            <option value="azure">Azure (Self-hosted T4)</option>
            <option value="runpod">RunPod Serverless (4090)</option>
            <option value="berget">Berget AI (KB-Whisper, Stockholm)</option>
          </select>
          {savingSettings && <span className="saving-indicator">Sparar...</span>}
        </div>
      </div>

      {/* Formulär för att skapa/uppdatera kliniker */}
      <form onSubmit={handleSubmit} className="clinic-form">
        <input
            type="text"
            name="clinic_name"
            placeholder="Namn på klinik"
            value={form.clinic_name}
            onChange={handleFormChange}
            required
            className="clinic-input"
          />

        {/* CHECKBOX FÖR INBYGGT JOURNALSYSTEM - Flyttad upp för logiskt flöde */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="use_internal_journal"
            checked={form.use_internal_journal}
            onChange={handleFormChange}
          />
          Använd inbyggt journalsystem
        </label>

        {/* CHECKBOX FÖR AION SPORTS */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="use_sports_module"
            checked={form.use_sports_module}
            onChange={handleFormChange}
          />
          Aktivera Aion Sports
        </label>

        {/* DROP-DOWN FÖR EXTERNT JOURNALSYSTEM (överföring) */}
        <label className="clinic-select-label">Journalsystem för överföring:</label>
        <select
          name="journal_system"
          value={form.journal_system}
          onChange={handleFormChange}
          className="clinic-select"
        >
          <option value="">Ingen</option>
          <option value="Kaddio">Kaddio</option>
          <option value="Clinicbuddy">Clinicbuddy</option>
          <option value="Webdoc">Webdoc</option>
          <option value="PMO">PMO</option>
          <option value="TakeCare">TakeCare</option>
          <option value="SLS_PDF">SLS_PDF</option>
        </select>

        {/* SMS-AVSÄNDARE */}
        <div className="clinic-sms-sender">
          <label>SMS-avsändare:</label>
          <input
            type="text"
            name="sms_sender"
            placeholder="t.ex. Kliniken"
            value={form.sms_sender}
            onChange={handleFormChange}
            maxLength={11}
            className="clinic-input sms-input"
          />
          <span className="sms-hint">Max 11 tecken, visas som avsändare vid SMS</span>
        </div>

        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
          <button type="submit" style={{ flex: 1 }}>
            {isEditing ? 'Uppdatera Klinik' : 'Skapa Klinik'}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="cancel-button"
              style={{ flex: 0 }}
            >
              Avbryt
            </button>
          )}
        </div>
      </form>

      {/* Lista med kliniker */}
      <table className="clinic-table">
        <thead>
          <tr>
            <th>Klinik-ID</th>
            <th>Kliniknamn</th>
            <th>Journalsystem</th>
            <th>Inbyggt journal</th>
            <th>Sports</th>
            <th>SMS-avsändare</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {clinics.map((clinic) => (
            <tr key={clinic.clinic_id}>
              <td>{clinic.clinic_id}</td>
              <td>{clinic.clinic_name}</td>
              <td className="clinic-journal-system">
                {clinic.journal_system || <span className="status-inactive">-</span>}
              </td>
              <td className="clinic-journal-status">
                {clinic.use_internal_journal ? (
                  <span className="status-active">Aktivt</span>
                ) : (
                  <span className="status-inactive">-</span>
                )}
              </td>
              <td className="clinic-sports-status">
                {clinic.use_sports_module ? (
                  <span className="status-active">Aktivt</span>
                ) : (
                  <span className="status-inactive">-</span>
                )}
              </td>
              <td className="clinic-sms-status">
                {clinic.sms_sender || <span className="status-inactive">-</span>}
              </td>
              <td>
                <button onClick={() => handleEdit(clinic)}>Redigera</button>
                <button
                  onClick={() => handleDelete(clinic.clinic_id)}
                  className="delete-button"
                >
                  Ta Bort
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Clinics;
